import { FaRegEnvelope } from "react-icons/fa";
import { useRef, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./SendOTP2.scss";
import "./SendOTP2Mobile.scss";

export default function SendOTP2({ onClose, emailForForgot, setModalOpen }) {
  const navigate = useNavigate();
  const inputs = useRef([]);
  const [isFilled, setIsFilled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [countdown, setCountdown] = useState(30); // ⏱ countdown in seconds
  const [resending, setResending] = useState(false);

  // 👇 Run once when component mounts
  useEffect(() => {
    if (inputs.current[0]) inputs.current[0].focus();
  }, []);

  // 👇 Run repeatedly for countdown updates
  useEffect(() => {
    if (countdown > 0) {
      const timer = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [countdown]);

  const checkAllFilled = () => {
    const allFilled = inputs.current.every(
      (input) => input?.dataset.filled === "true"
    );
    setIsFilled(allFilled);
  };

  const handleChange = (e, index) => {
    const raw = e.target.value;
    const digit = raw.replace(/[^0-9]/g, "").slice(-1);
    const input = inputs.current[index];

    if (input.dataset.filled === "true") {
      e.target.value = input.value || "";
      return;
    }

    if (digit) {
      input.value = digit;
      input.dataset.filled = "true";
      if (index < inputs.current.length - 1) inputs.current[index + 1].focus();
    } else {
      input.value = "";
      input.dataset.filled = "false";
    }

    checkAllFilled();
  };

  const handleKeyDown = (e, index) => {
    const input = inputs.current[index];

    if (e.key === "Backspace") {
      if (input.value) {
        input.value = "";
        input.dataset.filled = "false";
        e.preventDefault();
        checkAllFilled();
        return;
      }

      if (index > 0) {
        const prev = inputs.current[index - 1];
        prev.value = "";
        prev.dataset.filled = "false";
        prev.focus();
        e.preventDefault();
      }

      checkAllFilled();
    }

    const isPrintable =
      e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;
    if (isPrintable && input.dataset.filled === "true") e.preventDefault();

    if (e.key === "ArrowLeft" && index > 0) {
      inputs.current[index - 1].focus();
      e.preventDefault();
    } else if (e.key === "ArrowRight" && index < inputs.current.length - 1) {
      inputs.current[index + 1].focus();
      e.preventDefault();
    }
  };

  // 🔹 Verify OTP
  const handleVerify = async () => {
    const email = emailForForgot;
    const otp = inputs.current.map((input) => input.value).join("");
    if (otp.length < 6) return;

    try {
      setLoading(true);
      setError("");

      const res = await fetch(
        `${process.env.REACT_APP_BASE_URL}api/auth/verify-otp-forgot-pass`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ email, otp }),
        }
      );

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || "Invalid OTP. Please try again.");
        return;
      }

      alert("✅ OTP verified! You can now change your password");
      setModalOpen("ChangePass");
    } catch (err) {
      console.error(err);
      setError("Network error. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // 🔁 Resend OTP
  const handleResend = async () => {
    const email = emailForForgot;
    if (countdown > 0 || resending) return;

    try {
      setResending(true);
      setError("");

      const res = await fetch(
        `${process.env.REACT_APP_BASE_URL}api/auth/resend-otp`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email }),
        }
      );

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || "Failed to resend OTP. Try again later.");
        return;
      }

      alert("📩 A new OTP has been sent to your email.");
      setCountdown(30); // restart countdown
    } catch (err) {
      console.error(err);
      setError("Network error. Please try again later.");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="otp-container2">
      <div className="otp-icon-wrapper">
        <FaRegEnvelope />
      </div>
      <h2>OTP Verification</h2>
      <span>Check your email for a 6-digit code</span>

      <div className="input-OTP-wrapper">
        {[...Array(6)].map((_, i) => (
          <input
            key={i}
            type="text"
            inputMode="numeric"
            maxLength="1"
            data-filled="false"
            ref={(el) => (inputs.current[i] = el)}
            onChange={(e) => handleChange(e, i)}
            onKeyDown={(e) => handleKeyDown(e, i)}
            onFocus={() => {
              for (let j = 0; j < i; j++) {
                if (inputs.current[j].dataset.filled !== "true") {
                  inputs.current[j].focus();
                  return;
                }
              }
            }}
          />
        ))}
      </div>

      {error && <p style={{ color: "red" }}>{error}</p>}

      <p>
        Didn’t get the code?{" "}
        {countdown > 0 ? (
          <span style={{ color: "#888" }}>Resend in {countdown}s</span>
        ) : (
          <strong
            style={{
              color: "#007bff",
              cursor: "pointer",
            }}
            onClick={handleResend}
          >
            {resending ? "Sending..." : "Resend"}
          </strong>
        )}
      </p>

      <button
        disabled={!isFilled || loading}
        className={isFilled ? "verify-active" : "verify-disabled"}
        onClick={handleVerify}
      >
        {loading ? "Verifying..." : "VERIFY"}
      </button>
    </div>
  );
}
